import * as React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { authStorage } from '@/lib/authStorage';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';

export function AuthCallback() {
  const [params] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [outcome, setOutcome] = React.useState<'pending' | 'ok' | 'fail'>('pending');
  const [redirectTo, setRedirectTo] = React.useState<string>('/dashboard');

  React.useEffect(() => {
    // admin-auth emits `#jwt=<token>` in the URL fragment for the panel flow
    // (fragments are not sent to servers, so the token never lands in
    // Firebase Hosting access logs or upstream proxy logs). The `?jwt` /
    // `?token` query-param fallbacks preserve:
    //   1. the legacy `/auth/jwt` paste-token viewer
    //   2. any bookmark/email link created while the prior ?jwt flow was live
    const hashParams =
      typeof window !== 'undefined' && window.location.hash
        ? new URLSearchParams(window.location.hash.replace(/^#/, ''))
        : null;
    const token = hashParams?.get('jwt') ?? params.get('jwt') ?? params.get('token');

    if (!token) {
      setOutcome('fail');
      return;
    }
    const ok = loginWithToken(token);
    if (!ok) {
      setOutcome('fail');
      return;
    }
    const stored = authStorage.getReturnTo();
    const returnTo = hashParams?.get('returnTo') ?? params.get('returnTo');
    authStorage.clearReturnTo();

    // Scrub both the hash and query so the token doesn't persist in browser
    // history. It's now in localStorage; its URL carrier has served its
    // single-use purpose.
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    setRedirectTo(returnTo ?? stored ?? '/dashboard');
    setOutcome('ok');
  }, [params, loginWithToken]);

  if (outcome === 'ok') return <Navigate to={redirectTo} replace />;
  if (outcome === 'fail')
    return (
      <ErrorState
        title="Sign-in failed"
        description="The admin backend did not return a valid token. Try signing in again."
        actionLabel="Back to login"
        actionHref="/login"
      />
    );
  return <LoadingState label="Completing sign-in" />;
}
